<template>
  <div
    v-if="user"
    class="user-site-documents"
  >
    <header class="documents-header">
      <div class="documents-header__icon">
        <feather-icon
          icon="FolderIcon"
          size="18"
        />
      </div>
      <div class="documents-header__text">
        <h6 class="mb-0">
          Approved Documents
        </h6>
        <p class="mb-0 documents-header__subtitle">
          {{ user.email }}
        </p>
      </div>
      <b-badge
        pill
        variant="light-success"
        class="documents-header__badge"
      >
        {{ documents.length }} approved
      </b-badge>
      <button
        type="button"
        class="documents-refresh"
        :disabled="dataLoad"
        title="Refresh"
        @click="fetchDocuments"
      >
        <feather-icon
          icon="RefreshCwIcon"
          size="15"
          :class="{ 'spin-icon': dataLoad }"
        />
      </button>
    </header>

    <div
      v-if="dataLoad"
      class="documents-empty"
    >
      <div class="documents-loading__spinner" />
      <p class="mb-0 text-muted">
        Loading approved documents…
      </p>
    </div>

    <div
      v-else-if="!documents.length"
      class="documents-empty"
    >
      <div class="documents-empty__icon">
        <feather-icon
          icon="InboxIcon"
          size="26"
        />
      </div>
      <h6 class="mb-25">
        No approved documents
      </h6>
      <p class="mb-0 text-muted small">
        Approved documents for this engineer will appear here.
      </p>
    </div>

    <div
      v-else
      class="documents-list"
    >
      <article
        v-for="doc in documents"
        :key="doc.id"
        class="document-row document-row--approved"
      >
        <div
          class="document-row__icon"
          :class="`document-row__icon--${getDocumentStyle(doc.file_type).variant}`"
        >
          <feather-icon
            :icon="getDocumentStyle(doc.file_type).icon"
            size="18"
          />
        </div>

        <div class="document-row__content">
          <div class="document-row__type">
            {{ formatFileType(doc.file_type) }}
          </div>
          <div class="document-row__label">
            {{ doc.file_label }}
          </div>
          <div class="document-row__dates">
            <span
              v-if="doc.uploaded_at"
              class="document-row__date"
            >
              <feather-icon
                icon="UploadCloudIcon"
                size="12"
              />
              Uploaded {{ formatDateTime(doc.uploaded_at) }}
            </span>
            <span
              v-if="doc.reviewed_at"
              class="document-row__date"
            >
              <feather-icon
                icon="EyeIcon"
                size="12"
              />
              Reviewed {{ formatDateTime(doc.reviewed_at) }}
            </span>
          </div>
          <p
            v-if="doc.reviewed_by && doc.reviewed_by.name"
            class="document-row__reviewer mb-0"
          >
            Reviewed by {{ doc.reviewed_by.name }}
          </p>
        </div>

        <div class="document-row__side">
          <b-badge
            pill
            variant="light-success"
            class="document-row__status"
          >
            <feather-icon
              icon="CheckCircleIcon"
              size="12"
              class="mr-25"
            />
            Approved
          </b-badge>

          <a
            v-if="doc.drive_link"
            :href="doc.drive_link"
            target="_blank"
            rel="noopener noreferrer"
            class="document-action document-action--view"
            title="Open document"
          >
            <feather-icon
              icon="ExternalLinkIcon"
              size="15"
            />
          </a>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import formatDate from '@/global-compositions/formatDate'

const DOCUMENT_STYLES = {
  university_degree: { icon: 'AwardIcon', variant: 'primary' },
  contracts_agreements: { icon: 'FileTextIcon', variant: 'info' },
  code_of_ethics: { icon: 'ShieldIcon', variant: 'success' },
  appointment_letter: { icon: 'MailIcon', variant: 'warning' },
  resume: { icon: 'UserIcon', variant: 'danger' },
  signed_job_descriptions: { icon: 'BriefcaseIcon', variant: 'secondary' },
  passport: { icon: 'CreditCardIcon', variant: 'info' },
  visa: { icon: 'GlobeIcon', variant: 'primary' },
  labour_card: { icon: 'BriefcaseIcon', variant: 'warning' },
}

const DEFAULT_STYLE = { icon: 'FileIcon', variant: 'primary' }

export default {
  name: 'UserSiteDocumentsTab',
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      dataLoad: false,
      documents: [],
    }
  },
  watch: {
    'user.email': {
      immediate: true,
      handler() {
        this.fetchDocuments()
      },
    },
  },
  methods: {
    async fetchDocuments() {
      if (!this.user?.email) {
        this.documents = []
        return
      }

      this.dataLoad = true
      try {
        const response = await axios.get('user-required-documents?status=approved', {
          params: { search: this.user.email },
        })

        if (response.status === 200) {
          const { data } = decryptData(response.data.payload)
          const users = Array.isArray(data?.users) ? data.users : []
          this.documents = users.flatMap(item => item.documents || [])
        }
      } catch (error) {
        this.documents = []
        this.showError(error, 'Failed to load documents')
      } finally {
        this.dataLoad = false
      }
    },
    getDocumentStyle(fileType) {
      return DOCUMENT_STYLES[fileType] || DEFAULT_STYLE
    },
    formatFileType(fileType) {
      if (!fileType) return 'Document'
      return String(fileType)
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    formatDateTime(value) {
      return formatDate(value) || value
    },
    showError(error, fallback) {
      let message = fallback
      try {
        if (error?.response?.data?.payload) {
          const payload = decryptData(error.response.data.payload)
          const msg = payload?.message || payload
          if (typeof msg === 'string') message = msg
          else if (typeof msg === 'object') {
            const firstList = Object.values(msg).find(Array.isArray)
            if (firstList) {
              const [firstMessage] = firstList
              if (firstMessage) message = firstMessage
            }
          }
        }
      } catch {
        // keep fallback
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: 'BellIcon',
          variant: 'danger',
        },
      })
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/pages/user-site/user-site-documents-tab.scss"></style>
